import { webPortalApi } from '#config/web-portal-api';
import { getRequestOptions } from '#helper';

/**
 * Provides methods to control service worker.
 */

class ServiceWorkerController {
  /**
   * Converts string to unit8 array.
   * @param {string} base64String - base64String.
   * @returns {Uint8Array} unit8 array.
   */

  static urlB64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    // eslint-disable-next-line no-useless-escape
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Register service worker.
   * @returns {Promise<ServiceWorkerRegistration>} service worker registration.
   */

  static async registerServiceWorker() {
    const swRegistration = await navigator.serviceWorker.register(`${window.origin}/web-worker/push-service.js`);
    return swRegistration;
  }

  /**
   * Subscribe service worker on web-push.
   * @param {ServiceWorkerRegistration} swRegistration - service worker registration.
   * @returns {Promise<PushSubscription>} subscription
   */

  static async subscribeSw(swRegistration) {
    const res = await fetch(webPortalApi.buildApiUrl('web-push-public-key'), getRequestOptions());
    const { applicationServerKey } = await res.json();
    const applicationServerKeyBuffer = this.urlB64ToUint8Array(applicationServerKey);
    const options = { applicationServerKey: applicationServerKeyBuffer, userVisibleOnly: true };
    const subscription = await swRegistration.pushManager.subscribe(options);
    return subscription;
  }

  /**
   * Save subscription on server.
   * @param {PushSubscription} subscription - subscription.
   * @returns {Promise<Object>} - server response.
   */

  static async saveSubscription(subscription) {
    const res = await fetch(webPortalApi.buildApiUrl('subscription'), {
      method: 'POST',
      body: JSON.stringify({ subscription }),
      ...getRequestOptions()
    });
    const response = await res.json();
    return response;
  }

  /**
   * Delete subscriptions from server.
   * @param {PushSubscription[]} subscriptions - array of subscriptions.
   * @returns {Promise<void>}
   */

  static async deleteSubscriptions(subscriptions) {
    await fetch(webPortalApi.buildApiUrl('subscription'), {
      method: 'DELETE',
      body: JSON.stringify(subscriptions),
      ...getRequestOptions()
    });
  }

  /**
   * Check if subscription is present and belongs to a current user.
   * @param {PushSubscription} subscription - subscription.
   * @returns {Promise<boolean>}
   */

  static async checkSubscription(subscription) {
    const res = await fetch(webPortalApi.buildApiUrl('subscription/check'), {
      method: 'POST',
      body: JSON.stringify({ subscription }),
      ...getRequestOptions()
    });
    const { present } = await res.json();
    return present;
  }

  /**
   * Initialize service worker.
   * Process valid subscription.
   * @returns {Promise<void>}
   */

  static async initSW() {
    let permission = null;
    // Chrome in iOS v13.3 (iPhone7) doesn't have `Notification` object locally
    // (used local machine ip)
    if (window.Notification) {
      permission = await Notification.requestPermission();
    }
    if (permission === 'granted' && 'serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const swRegistration = await this.registerServiceWorker();
        const currentSubscription = await swRegistration.pushManager.getSubscription();
        if (!currentSubscription) {
          // Subscribe
          const subscription = await this.subscribeSw(swRegistration);
          await this.saveSubscription(subscription);
        } else {
          const isPresent = await this.checkSubscription(currentSubscription);
          if (!isPresent) {
            // Unsubscribe
            await currentSubscription.unsubscribe();
            // Subscribe to new
            const subscription = await this.subscribeSw(swRegistration);
            await this.saveSubscription(subscription);
          }
        }
      } catch (e) {
        console.warn(e);
      }
    }
  }

  /**
   * Unregister service worker.
   * Unsubscribe and delete subscriptions.
   * @returns {Promise<void>}
   */

  static async unregisterSW() {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length) {
        try {
          const subscriptions = await Promise.all(
            registrations.map(r => r.pushManager.getSubscription())
          );
          const filteredSubscriptions = subscriptions.filter(Boolean);

          if (filteredSubscriptions.length) {
            await Promise.all([
              Promise.all(filteredSubscriptions.map(s => s.unsubscribe())),
              this.deleteSubscriptions(filteredSubscriptions)
            ]);
          }
        } catch (e) {
          console.warn(e);
        } finally {
          await Promise.all(registrations.map(r => r.unregister()));
        }
      }
    } catch (e) {
      console.warn(e);
    }
  }
}

export default ServiceWorkerController;
